/**
 * useMagnetic — CTA buttons attract toward cursor on hover.
 *
 * No-op on touch devices (pointer: coarse) and prefers-reduced-motion.
 * Returns spring-animated MotionValues for x/y that the caller applies
 * via `style={{ x, y }}` on a `motion.*` element.
 */

import { useRef, useCallback } from 'react';
import { useSpring, type MotionValue } from 'motion/react';

interface MagneticOptions {
  strength?: number;
  radius?: number;
}

interface MagneticReturn {
  x: MotionValue<number>;
  y: MotionValue<number>;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseLeave: () => void;
}

const isCoarse =
  typeof window !== 'undefined' &&
  window.matchMedia('(pointer: coarse)').matches;

const prefersReduced =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useMagnetic(
  ref: React.RefObject<HTMLElement | null>,
  { strength = 8, radius = 80 }: MagneticOptions = {},
): MagneticReturn {
  const springConfig = { stiffness: 150, damping: 15, mass: 0.1 };
  const x = useSpring(0, springConfig);
  const y = useSpring(0, springConfig);
  const disabled = useRef(isCoarse || prefersReduced);

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (disabled.current || !ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      x.set(Math.max(-strength, Math.min(strength, (dx / radius) * strength)));
      y.set(Math.max(-strength, Math.min(strength, (dy / radius) * strength)));
    },
    [ref, strength, radius, x, y],
  );

  const onMouseLeave = useCallback(() => {
    x.set(0);
    y.set(0);
  }, [x, y]);

  return { x, y, onMouseMove, onMouseLeave };
}
